import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, signal } from "@angular/core";
import { takeUntilDestroyed, toObservable, toSignal } from "@angular/core/rxjs-interop";
import { AccountType, AdStats, GlobalStats, StrategyStateEnum } from "@front/m19-api-client";
import {
  ACOS,
  AD_CONVERSIONS,
  AD_SALES,
  CLICK_THROUGH_RATE,
  CLICKS,
  CONVERSION_RATE,
  COST,
  CPC,
  IMPRESSIONS,
  Metric,
  ORDER_GLOBAL_MARGIN,
  ORDER_GLOBAL_NET_SALES,
  PROFIT,
  ROAS,
  TACOS,
  TOTAL_ORDERS,
  TOTAL_SALES,
} from "@front/m19-metrics";
import { AdStatsEx, CurrencyStat, OrderStats } from "@front/m19-models";
import { DataSetEventAnnotation } from "@front/m19-models/dataset";
import { DefaultAdStatsMetricsWithSameScale } from "@front/m19-models/dataset/Dataset";
import {
  AccountSelectionService,
  AsinService,
  AuthService,
  DspStrategyService,
  MetricsSelectorPage,
  MetricsSelectorKey,
  SbStrategyService,
  SdStrategyService,
  SpStrategyService,
  StatsService,
  UserSelectionService,
} from "@front/m19-services";
import { ChartConfig, IButtonDirective, IChartComponent, IIconComponent, ITooltipDirective } from "@front/m19-ui";
import {
  convertDspStatToAdStats,
  DateAggregation,
  DateUtils,
  getOrderStatsFromOrder,
  getVendorAsinSalesStats,
  mergeAllStats,
  sumAdStats,
} from "@front/m19-utils";
import { TranslocoDirective } from "@jsverse/transloco";
import { ChartLayoutComponent } from "@m19-board/containers/chart-layout/chart-layout.component";
import { MetricSelectorComponent } from "@m19-board/insights/metric-selector/metric-selector.component";
import {
  APP_RATIO,
  BUY_BOX_RATE,
  COST_OF_GOODS,
  ORDER_GLOBAL_FEE,
  PAGE_VIEWS,
  POINTS,
  PROMOTION,
  RAW_SALES,
  REFUNDED_SALES,
  SESSIONS,
  SPONSORED_SALES_SHARE,
  TOTAL_CONVERSION_RATE,
  VENDOR_CUSTOMER_RETURNS,
  VENDOR_SHIPPED_COGS,
} from "@m19-board/models/MetricsDef";
import {
  AdType,
  CompactFilterComponent,
  Filter,
  FilterCategory,
} from "@m19-board/shared/compact-filter/compact-filter.component";
import { AsinFilter, AsinFilterService } from "@m19-board/shared/filter/asin-filter.service";
import { map, Observable, of, shareReplay, switchMap, tap } from "rxjs";
import { combineLatest } from "rxjs/internal/observable/combineLatest";
import { ActivityEventAnnotationsService } from "../activities/activity-event-annotations.service";
import { ProductAnalysisGridComponent, ProductAnalysisGridData } from "./product-analysis-grid.component";

export const GLOBAL_CHART_METRICS_VENDOR: Metric<CurrencyStat>[] = [
  AD_SALES,
  AD_CONVERSIONS,
  COST,
  ACOS,
  CLICKS,
  IMPRESSIONS,
  CPC,
  CONVERSION_RATE,
  ROAS,
  PAGE_VIEWS,
  CLICK_THROUGH_RATE,
  TOTAL_SALES,
  TOTAL_ORDERS,
  TACOS,
  SPONSORED_SALES_SHARE,
  VENDOR_SHIPPED_COGS,
  VENDOR_CUSTOMER_RETURNS,
];

export const GLOBAL_CHART_METRICS_SELLER: Metric<CurrencyStat>[] = [
  TOTAL_SALES,
  TOTAL_ORDERS,
  TACOS,
  AD_SALES,
  AD_CONVERSIONS,
  COST,
  ACOS,
  CLICKS,
  IMPRESSIONS,
  CPC,
  CONVERSION_RATE,
  ROAS,
  SPONSORED_SALES_SHARE,
  SESSIONS,
  PAGE_VIEWS,
  APP_RATIO,
  BUY_BOX_RATE,
  TOTAL_CONVERSION_RATE,
  CLICK_THROUGH_RATE,
  ORDER_GLOBAL_FEE,
  COST_OF_GOODS,
  PROMOTION,
  POINTS,
  PROFIT,
  ORDER_GLOBAL_MARGIN,
  REFUNDED_SALES,
  RAW_SALES,
  ORDER_GLOBAL_NET_SALES,
] as Metric<CurrencyStat>[];

export function convertToAdStats(stats: GlobalStats[], selectedAdTypes: AdType[]) {
  const data: AdStats[] = [];
  for (const stat of stats) {
    const adStats: AdStatsEx = {
      date: stat.date,
      asin: stat.asin,
    };
    if (stat.adStats && (selectedAdTypes.length == 0 || selectedAdTypes.includes(AdType.SponsoredAds))) {
      Object.assign(adStats, stat.adStats);
    }
    if (stat.salesAndTraffic) {
      Object.assign(adStats, stat.salesAndTraffic); // no currency conversion needed
    }
    Object.assign(
      adStats,
      getOrderStatsFromOrder(
        stat.profitOrder ?? { date: stat.date, asin: stat.asin },
        stat.adStats?.cost ?? 0,
        stat.dspStats?.totalCost ?? 0,
      ),
    );
    if (stat.vendorSales) {
      Object.assign(adStats, getVendorAsinSalesStats(stat.vendorSales));
    }
    if (stat.netPureProductMargin) {
      adStats.netPureProductMargin = stat.netPureProductMargin;
    }
    if (stat.dspStats && stat.adStats && (selectedAdTypes.length == 0 || selectedAdTypes.includes(AdType.DSP))) {
      const dspAdStatsConverted = convertDspStatToAdStats(stat.dspStats);
      sumAdStats(adStats, dspAdStatsConverted);
    }
    data.push(adStats);
  }
  return data;
}

@Component({
  selector: "app-product-analysis",
  templateUrl: "./product-analysis.component.html",
  imports: [
    MetricSelectorComponent,
    TranslocoDirective,
    IButtonDirective,
    ITooltipDirective,
    ChartLayoutComponent,
    ProductAnalysisGridComponent,
    CompactFilterComponent,
    IIconComponent,
    IChartComponent,
  ],
  providers: [AsinFilterService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProductAnalysisComponent {
  private readonly asinFilterService = inject(AsinFilterService);
  private readonly accountSelectionService = inject(AccountSelectionService);
  private readonly userSelectionService = inject(UserSelectionService);
  private readonly authService = inject(AuthService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly statsService = inject(StatsService);
  private readonly asinService = inject(AsinService);
  private readonly activityEventAnnotationsService = inject(ActivityEventAnnotationsService);
  private readonly spStrategyService = inject(SpStrategyService);
  private readonly sbStrategyService = inject(SbStrategyService);
  private readonly sdStrategyService = inject(SdStrategyService);
  private readonly dspStrategyService = inject(DspStrategyService);

  readonly localStorageKey: MetricsSelectorKey = { page: MetricsSelectorPage.salesAndAdvertising };
  readonly accountMarketplace = toSignal(this.accountSelectionService.singleAccountMarketplaceSelection$, {
    requireSync: true,
  });
  readonly currency = toSignal(this.userSelectionService.selectedCurrency$, { requireSync: true });
  readonly locale = toSignal(this.authService.loggedUser$.pipe(map((user) => user.locale)), { requireSync: true });
  readonly filters = toSignal(this.asinFilterService.getAsinFilters(true));

  readonly totalData = signal<CurrencyStat>({});
  readonly previousTotalData = signal<CurrencyStat>({});
  readonly gridData = signal<ProductAnalysisGridData[]>([]);
  readonly allEventAnnotationTypes = this.activityEventAnnotationsService.allActivityEventTypesOptions;
  readonly allStrategies = toSignal(this.activityEventAnnotationsService.allStrategiesOptions$);

  readonly globalChartMetrics = computed(() => this.getGlobalChartMetrics(this.accountMarketplace()?.accountType));
  readonly selectedMetrics = signal<Metric<CurrencyStat>[]>([AD_SALES, COST]);
  readonly dateAggregation = signal<DateAggregation>(DateAggregation.daily);
  readonly disableEventAnnotation = computed(() => this.dateAggregation() !== DateAggregation.daily);
  readonly isGlobalChartHidden = signal<boolean>(
    !this.userSelectionService.getUserChartDisplayedPreference(this.localStorageKey),
  );
  readonly globalDataLoading = signal<boolean>(true);
  readonly gridDataLoading = signal<boolean>(true);
  readonly displayEventAnnotation = signal<boolean>(false);
  readonly eventAnnotations$: Observable<DataSetEventAnnotation[]> = combineLatest([
    toObservable(this.displayEventAnnotation),
    toObservable(this.disableEventAnnotation),
    this.activityEventAnnotationsService.activityEventsAnnotations$,
  ]).pipe(map(([display, disableEventAnnotation, events]) => (display && !disableEventAnnotation ? events : [])));
  readonly eventAnnotations = toSignal(this.eventAnnotations$);
  readonly selectedAdTypes = signal<AdType[]>([AdType.SponsoredAds]);
  readonly selectedAdTypes$: Observable<AdType[]> = toObservable(this.selectedAdTypes);

  globalData$ = combineLatest([
    this.asinFilterService.filteredAsins$,
    this.userSelectionService.selectedDateRange$,
    this.accountSelectionService.singleAccountMarketplaceSelection$,
    this.userSelectionService.periodComparison$,
    this.userSelectionService.selectedCurrency$,
  ]).pipe(
    tap(() => {
      this.setLoadingState(true);
    }),
    switchMap(([asins, dateRange, accountMarketplace, comparison, currency]) => {
      return combineLatest([
        this.statsService.getDailyProductStats(
          accountMarketplace.accountId,
          accountMarketplace.marketplace,
          currency,
          DateUtils.formatDateForApi(dateRange.start.toJSDate()),
          DateUtils.formatDateForApi(dateRange.end.toJSDate()),
          accountMarketplace.useSourcingMetrics ?? false,
          accountMarketplace.dspAdvertiserId,
          asins === null ? undefined : Array.from(asins),
        ),
        comparison?.period
          ? this.statsService.getDailyProductStats(
              accountMarketplace.accountId,
              accountMarketplace.marketplace,
              currency,
              comparison.period[0],
              comparison.period[1],
              accountMarketplace.useSourcingMetrics ?? false,
              accountMarketplace.dspAdvertiserId,
              asins === null ? undefined : Array.from(asins),
            )
          : of([]),
      ]).pipe(
        switchMap(([stats, previousStats]) => {
          return this.selectedAdTypes$.pipe(
            map((selectedAdTypes) => {
              const adTypes = accountMarketplace.dspAdvertiserId ? selectedAdTypes : [AdType.SponsoredAds];
              const data: AdStats[] = convertToAdStats(stats, adTypes);
              const previousData: AdStats[] = convertToAdStats(previousStats, adTypes);
              return {
                data,
                previousData,
                dateRange,
                asins,
                accountMarketplace,
                previousDateRange: comparison?.period,
                currency,
              };
            }),
          );
        }),
      );
    }),
    tap({
      next: () => {
        this.setLoadingState(false);
      },
      error: () => {
        this.setLoadingState(false);
      },
    }),
    shareReplay(1),
  );

  globalData = toSignal(this.globalData$);
  dateRange = toSignal(this.userSelectionService.selectedDateRange$, { requireSync: true });

  readonly chartDatasetConfig = computed<ChartConfig<AdStats & OrderStats, "line">>(() => ({
    type: "line",
    aggFunctions: [mergeAllStats],
    metricsOnSameScale: DefaultAdStatsMetricsWithSameScale,
    aspectRatio: 3,
    metrics: this.selectedMetrics(),
    dateAggregation: this.dateAggregation(),
    dateRange: this.dateRange(),
    eventAnnotations: this.eventAnnotations(),
    previousPeriodData: (this.globalData()?.previousData as (AdStatsEx & OrderStats)[]) ?? [],
    previousPeriod: this.globalData()?.previousDateRange,
  }));

  constructor() {
    this.globalData$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(({ data, previousData }) => {
      this.totalData.set(
        data.reduce(
          (acc, curr) => mergeAllStats(acc as OrderStats & AdStatsEx, curr as OrderStats & AdStatsEx),
          {} as OrderStats & AdStatsEx,
        ),
      );
      this.previousTotalData.set(
        previousData.reduce(
          (acc, curr) => mergeAllStats(acc as OrderStats & AdStatsEx, curr as OrderStats & AdStatsEx),
          {} as OrderStats & AdStatsEx,
        ),
      );
    });

    // Grid data is populated after line chart data to avoid duplicate calls
    this.globalData$
      .pipe(
        switchMap(({ dateRange, previousDateRange, asins, accountMarketplace, currency }) => {
          this.gridDataLoading.set(true);
          return combineLatest([
            this.statsService
              .getAsinProductStats(
                accountMarketplace.accountId,
                accountMarketplace.marketplace,
                currency,
                DateUtils.formatDateForApi(dateRange.start.toJSDate()),
                DateUtils.formatDateForApi(dateRange.end.toJSDate()),
                accountMarketplace.useSourcingMetrics ?? false,
                accountMarketplace.dspAdvertiserId,
                asins === null ? undefined : Array.from(asins),
              )
              .pipe(
                shareReplay(1),
                switchMap((stats) => {
                  return this.selectedAdTypes$.pipe(
                    map((selectedAdTypes) => {
                      const adTypes = accountMarketplace.dspAdvertiserId ? selectedAdTypes : [AdType.SponsoredAds];
                      const data: AdStats[] = convertToAdStats(stats, adTypes);
                      return data;
                    }),
                  );
                }),
              ),
            previousDateRange
              ? this.statsService
                  .getAsinProductStats(
                    accountMarketplace.accountId,
                    accountMarketplace.marketplace,
                    currency,
                    previousDateRange[0],
                    previousDateRange[1],
                    accountMarketplace.useSourcingMetrics ?? false,
                    accountMarketplace.dspAdvertiserId,
                    asins === null ? undefined : Array.from(asins),
                  )
                  .pipe(
                    shareReplay(1),
                    switchMap((stats) => {
                      return this.selectedAdTypes$.pipe(
                        map((selectedAdTypes) => {
                          const adTypes = accountMarketplace.dspAdvertiserId ? selectedAdTypes : [AdType.SponsoredAds];
                          const data: AdStats[] = convertToAdStats(stats, adTypes);
                          return data;
                        }),
                      );
                    }),
                  )
              : of(undefined),
            this.asinService.getCatalog(accountMarketplace.accountId, accountMarketplace.marketplace),
            this.spStrategyService.getStrategiesPerAsin(accountMarketplace.accountId, accountMarketplace.marketplace),
            this.sbStrategyService.getStrategiesPerAsin(accountMarketplace.accountId, accountMarketplace.marketplace),
            this.sdStrategyService.getStrategiesPerAsin(accountMarketplace.accountId, accountMarketplace.marketplace),
            this.dspStrategyService.getStrategiesPerAsin(accountMarketplace.accountId, accountMarketplace.marketplace),
          ]);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(
        ([data, previousData, catalog, spAsinStratIndex, sbAsinStratIndex, sdAsinStratIndex, dspAsinStratIndex]) => {
          // enhance data with product info from catalog and from previous data for comparison
          for (const row of data as Array<ProductAnalysisGridData>) {
            const productInfo = catalog.asinOffers.get(row.asin!);
            if (productInfo) {
              Object.assign(row, {
                productTitle: productInfo.title,
                brand: productInfo.brand,
                parentAsin: productInfo.parentAsin ?? row.asin,
                productTag1: productInfo.productTag1,
                productTag2: productInfo.productTag2,
              });
            }
            if (previousData) {
              const previousRow = previousData.find((r) => r.asin === row.asin);
              if (previousRow) {
                row.previousData = previousRow;
              }
            }
            row.activeStrategies = [];
            spAsinStratIndex.get(row.asin!)?.forEach((s) => {
              if (s.state == StrategyStateEnum.ENABLED) row.activeStrategies!.push(s);
            });
            sbAsinStratIndex.get(row.asin!)?.forEach((s) => {
              if (s.state == StrategyStateEnum.ENABLED) row.activeStrategies!.push(s);
            });
            sdAsinStratIndex.get(row.asin!)?.forEach((s) => {
              if (s.state == StrategyStateEnum.ENABLED) row.activeStrategies!.push(s);
            });
            dspAsinStratIndex.get(row.asin!)?.forEach((s) => {
              if (s.state == StrategyStateEnum.ENABLED) row.activeStrategies!.push(s);
            });
          }
          this.gridData.set(data);
          this.gridDataLoading.set(false);
        },
      );
  }

  private getGlobalChartMetrics(accountType: AccountType | undefined) {
    return accountType == AccountType.VENDOR ? GLOBAL_CHART_METRICS_VENDOR : GLOBAL_CHART_METRICS_SELLER;
  }

  applyFilter(filters: Filter<AsinFilter>[]) {
    if (filters.some((f) => f.type === FilterCategory.AdType)) {
      this.selectedAdTypes.set(filters.find((f) => f.type === FilterCategory.AdType)?.values as AdType[]);
    }
    this.asinFilterService.selectFilters(filters);
  }

  selectMetrics(metrics: Metric<CurrencyStat>[]) {
    this.selectedMetrics.set(metrics as Metric<CurrencyStat>[]);
  }

  toggleGlobalChartDisplay(hide: boolean) {
    this.isGlobalChartHidden.set(hide);
    this.userSelectionService.setUserChartDisplayedPreference(this.localStorageKey, !this.isGlobalChartHidden());
  }

  toggleEventAnnotationDisplay(value: boolean) {
    this.displayEventAnnotation.set(value);
  }

  selectAggregation(aggregation: DateAggregation) {
    this.dateAggregation.set(aggregation);
  }

  private setLoadingState(loading: boolean) {
    this.globalDataLoading.set(loading);
    this.gridDataLoading.set(loading);
  }
}
